import { cn } from "@/lib/utils";

/**
 * The one definition of the Hero Rank bands, and the one component that draws the key for them.
 *
 * Every screen that colours a hero rank reads this: the Keywords tab's `RankingCell`, the A/B
 * Keyword Bank picker's `HeroRankPill`, and the column-header key on both. Before this there were
 * five unlinked copies of the same two numbers -- two sets of thresholds and three sets of
 * hard-typed label text -- and they had drifted twice:
 *
 *   AUT-258  the key said "Green 1-10 / Red 51+" and never mentioned yellow, while the cell had
 *            been 15/49 for months.
 *   AUT-442  the cell split at 49 and the A/B pill at 50, so rank 50 was red on one screen and
 *            amber on the other. Kate settled it at 1-15 / 16-50 / 51+ / unranked.
 *   AUT-442  (QA round 2) the key was hanging off the Keywords column, which it says nothing about.
 *
 * Each time, the fix corrected the copies that were noticed. This removes the copies.
 *
 * **Colour is still per-surface, and deliberately so.** The Keywords tab paints the soft status
 * tokens; the A/B picker paints the heat ramp, which is what the prototype uses. That divergence is
 * real and predates this component -- unifying it means restyling `RankingCell`, which Landscapes,
 * SEO Strategy and the Keyword Bank metric cells also render, against
 * `decisions/the-rank-heat-scale-is-six-stops-on-a-log-ramp.md`. A human's call, not this
 * component's. So each caller passes the classes **its own cell paints with**, which is what keeps
 * a key honest about the table beside it.
 */
export const HERO_RANK_BANDS = { greenMax: 15, yellowMax: 50 } as const;

/** Background classes for the four states, as the calling surface's own cell paints them. */
export interface HeroRankSwatches {
  top: string;
  mid: string;
  terminal: string;
  unranked: string;
}

export function HeroRankColorKey({ swatches }: { swatches: HeroRankSwatches }) {
  const { greenMax, yellowMax } = HERO_RANK_BANDS;

  const rows: [string, string][] = [
    [swatches.top, `Rank 1–${greenMax}`],
    [swatches.mid, `Rank ${greenMax + 1}–${yellowMax}`],
    [swatches.terminal, `Rank ${yellowMax + 1}+`],
    // The unranked swatch is a near-background grey, so it needs the border to be visible at all.
    [cn(swatches.unranked, "border border-border"), "Unranked"],
  ];

  return (
    <>
      <p className="font-semibold mt-2 mb-1">Color key</p>
      <div className="space-y-1">
        {rows.map(([className, label]) => (
          <p key={label} className="flex items-center gap-2">
            <span className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", className)} />
            {label}
          </p>
        ))}
      </div>
    </>
  );
}
